import { useCallback, useEffect, useRef, useState } from 'react';
import NewCustomDataTable from '../../common/NewCustomDataTable.jsx';
import SyllabusForm from './SyllabusForm.jsx';
import PopupDone, { usePopupDone } from '../../common/PopupDone/PopupDone.jsx';
import '../../Trainer/TrainerDashboard.css';
import '../../common/lms-pages.css';
import { getcourses } from '../../../../services/CourseModule';
import {
  listSyllabusTopics,
  deleteSyllabusTopics,
} from '../../../../services/SyllabusTopicServices.js';
import { useNavigate } from 'react-router-dom';
import { confirm } from '../../common/Confirm/confirm';
import { useAuth } from '../../../../context/AuthContext';
import { toast } from '../../common/Toast/toast';
import FilterSheet from '../../common/FilterSheet/FilterSheet';
import useDebouncedCallback from '../../common/useDebouncedCallback';

/** Normalize Mongo/API ids and populated { id } joins for comparisons. */
const toIdString = (v) => {
  if (v == null || v === '') return '';
  if (typeof v === 'object') return String(v.id ?? v._id ?? '').trim();
  return String(v).trim();
};

const sameCourseId = (a, b) => {
  const x = toIdString(a);
  const y = toIdString(b);
  return x !== '' && x === y;
};

const isRootSyllabusTopic = (topic) => {
  const p = topic?.parentTopicId;
  return p == null || p === '' || p === 'NA' || String(p).toLowerCase() === 'null';
};

// New function to fetch topics for a course with exact limit
const fetchTopicsForCourseWithLimit = async (courseId, limit = 10) => {
  try {
    const response = await listSyllabusTopics({
      courseId: courseId,
      limit: limit,
      page: 1,
      search: "",
      sort: "desc",
    });
    return Array.isArray(response?.data) ? response.data : [];
  } catch (error) {
    console.error(`Error fetching topics for course ${courseId} with limit ${limit}:`, error);
    throw error;
  }
};

const ListSyllabus = () => {
  const [data, setData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchField, setSearchField] = useState('name');
  const [recordsPerPage, setRecordsPerPage] = useState(25);
  const [details, setDetails] = useState(null);
  // null = closed, { recordId: null } = adding, { recordId } = editing one
  // topic. `reopen` is the course whose sheet handed the edit over, so the
  // reader is put back where they were instead of at the bare list.
  const [formModal, setFormModal] = useState(null);
  // A change saved in either popup is confirmed in it (the done panel),
  // which then closes itself. The form, opened over the sheet, is newer.
  const { done: detailsDone } = usePopupDone(Boolean(details));
  const { done: formDone } = usePopupDone(Boolean(formModal));
  const [topicRows, setTopicRows] = useState([]);
  const [topicsLoading, setTopicsLoading] = useState(false);
  const detailsRef = useRef(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [uiSearchTerm, setUiSearchTerm] = useState('');

  // Read before the fetch awaits anything: a reset mid-flight would turn an
  // append into a replace and snap the list back to its first page.
  const appendRef = useRef(false);
  const loadingMoreRef = useRef(false);
  const listEndRef = useRef(null);

  const navigate = useNavigate();

  const hasShownToast = useRef(false);
  const { hasPermission, user } = useAuth();
  useEffect(() => {
    if (!hasPermission('list-syllabuses', 'add', 'lms')) {
      if (!hasShownToast.current) {
        toast.error('You do not have permission to view Syllabus');
        navigate('/');
        hasShownToast.current = true;
      }
    }
  }, [hasPermission, navigate]);

  // Format date to dd/mmm/yyyy
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, '0');
      const month = date.toLocaleString('en-US', { month: 'short' });
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (error) {
      return 'N/A';
    }
  };

  // Delete individual topic confirmation
  const handleDeleteTopic = (topicId, topicName) => {
    confirm({
      title: 'Delete Topic?',
      html: `
        <div>
          <p>You are about to delete:</p>
          <div>
            <h6>📚 ${topicName}</h6>
          </div>
          <p>
            ⚠️ <strong>Warning:</strong> This action will permanently delete this topic. This cannot be undone.
          </p>
        </div>
      `,
      tone: 'danger',
      confirmButtonText: 'Yes, Delete Topic',
      cancelButtonText: 'Cancel',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteSyllabusTopics(topicId);

          toast.success(`Topic "${topicName}" has been deleted successfully.`);

          appendRef.current = false;
          fetchData(currentPage, searchTerm);
          if (detailsRef.current) await loadTopics(detailsRef.current);
        } catch (error) {
          console.error('Error deleting topic:', error);
          toast.error('There was an error deleting the topic. Please try again.');
        }
      }
    });
  };

  // Delete course confirmation
  const handleDeleteCourse = (courseRow) => {
    confirm({
      title: 'Are you sure?',
      html: `
        <div>
          <p>You are about to delete:</p>
          <div>
            <h5>📚 ${courseRow.courseName}</h5>
            <p>This course has ${courseRow.topicCount} topics</p>
          </div>
          <p>
            ⚠️ <strong>Warning:</strong> This action will permanently delete the course and all associated topics. This cannot be undone.
          </p>
        </div>
      `,
      tone: 'danger',
      confirmButtonText: 'Yes, Delete Course',
      cancelButtonText: 'Cancel',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          // Use the new function with exact limit
          const courseTopics = await fetchTopicsForCourseWithLimit(courseRow.courseId, courseRow.topicCount);
          for (const topic of courseTopics) {
            await deleteSyllabusTopics(topic.id);
          }

          // Confirmed in the popup, which then closes itself.
          toast.success(`${courseRow.courseName} and all its topics have been deleted successfully.`);

          fetchData(currentPage, searchTerm);
        } catch (error) {
          console.error('Error deleting course:', error);
          toast.error('There was an error deleting the course. Please try again.');
        }
      }
    });
  };

  // Adding, and editing one topic, are the same dialog. An edit opens it
  // over the syllabus sheet, which stays up behind it.
  const openAddForm = () => {
    setDetails(null);
    setFormModal({ recordId: null, reopen: null });
  };

  // Closing the dialog -- Cancel, the close button, a click outside --
  // puts back the course sheet an edit came out of. So does the done panel
  // a save shows, once it closes.
  const closeForm = () => {
    const reopen = formModal?.reopen;
    setFormModal(null);
    if (reopen) setDetails(reopen);
  };

  const handleEditTopic = (topicId, courseRow) => {
    setFormModal({ recordId: topicId, reopen: courseRow || null });
  };

  // Editing the syllabus itself: the same dialog with the course already
  // chosen, so topics are added to this one without picking it again.
  const handleEditSyllabus = (courseRow) => {
    if (!courseRow) return;
    setFormModal({
      recordId: null,
      courseId: courseRow.courseId,
      reopen: courseRow,
    });
  };

  const fetchData = async (page = 1, search = '') => {
    const isAppend = appendRef.current;
    try {
      if (isAppend) setLoadingMore(true);
      else setLoading(true);

      const courseRes = await getcourses(recordsPerPage, page, search, { searchField: 'name' });
      const courseList = Array.isArray(courseRes?.data) ? courseRes.data : [];
      const pagination = courseRes?.pagination || {};

      const rows = courseList.map((course) => {
        const topicCount = Number(course.syllabusTopicCount) || 0;
        const preview = course.syllabusTopicPreview || '';
        const topicSummary =
          preview ||
          (topicCount > 0 ? `${topicCount} topic${topicCount !== 1 ? 's' : ''}` : 'No topics');
        const displayTopics =
          topicSummary.length > 20 ? topicSummary.substring(0, 20) + '...' : topicSummary;

        return {
          courseId: toIdString(course.id) || course.id,
          courseName: course.name,
          description: course.description,
          duration: course.duration,
          branchOfCourse: course.branchOfCourse,
          departmentOfCourse: course.departmentOfCourse,
          createdAt: course.createdAt,
          topicCount,
          displayTopics,
          fullTopicSummary: topicSummary,
        };
      });

      // Guarded by course id so a repeated page cannot double up a row.
      if (isAppend) {
        setData((prev) => {
          const seen = new Set(prev.map((r) => r.courseId));
          return [...prev, ...rows.filter((r) => !seen.has(r.courseId))];
        });
      } else {
        setData(rows);
      }
      appendRef.current = false;
      setTotalRecords(Number(pagination.total) || rows.length);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to fetch course and topic data. Please try again.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
      loadingMoreRef.current = false;
    }
  };

  useEffect(() => {
    fetchData(currentPage, searchTerm);
  }, [currentPage, searchTerm, recordsPerPage]);

  // Topics arrive flat with a parentTopicId, and are read as a tree: a
  // parent followed by its own children, then anything orphaned. Flattening
  // in that order is what lets the dialog number them 1..n down one column
  // while still showing which are sub-topics.
  const flattenTopics = (topics, courseId) => {
    const mine = (topics || []).filter((t) => sameCourseId(t.courseId, courseId));
    const byAge = (a, b) => new Date(a.createdAt) - new Date(b.createdAt);

    const parents = mine.filter(isRootSyllabusTopic).sort(byAge);
    const parentIds = new Set(parents.map((t) => t.id));
    const childrenOf = (id) =>
      mine.filter((t) => !isRootSyllabusTopic(t) && t.parentTopicId === id).sort(byAge);

    const rows = [];
    parents.forEach((parent) => {
      rows.push({ ...parent, isChild: false });
      childrenOf(parent.id).forEach((child) =>
        rows.push({ ...child, isChild: true }),
      );
    });

    // A topic whose parent is not in this course still has to appear.
    mine
      .filter((t) => !isRootSyllabusTopic(t) && !parentIds.has(t.parentTopicId))
      .sort(byAge)
      .forEach((t) => rows.push({ ...t, isChild: false }));

    return rows;
  };

  const loadTopics = useCallback(async (courseRow) => {
    if (!courseRow) return;
    setTopicsLoading(true);
    try {
      const topics = await fetchTopicsForCourseWithLimit(
        courseRow.courseId,
        courseRow.topicCount || 100,
      );
      setTopicRows(flattenTopics(topics, courseRow.courseId));
    } catch (e) {
      console.error(e);
      setTopicRows([]);
      toast.error('Could not load topics for this course.');
    } finally {
      setTopicsLoading(false);
    }
  }, []);

  useEffect(() => {
    detailsRef.current = details;
    if (!details) {
      setTopicRows([]);
      return;
    }
    loadTopics(details);
  }, [details, loadTopics]);

  const hasMore = data.length < totalRecords;

  // The foot of the list asks for the next page when it comes into view.
  useEffect(() => {
    const node = listEndRef.current;
    if (!node || !hasMore || loading) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        // `loading` is state and lags a frame, so the ref is what stops one
        // pass asking for two pages.
        if (loadingMoreRef.current) return;
        loadingMoreRef.current = true;
        appendRef.current = true;
        setLoadingMore(true);
        setCurrentPage((p) => p + 1);
      },
      { rootMargin: '150px' },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [hasMore, loading, data.length]);

  const handlePageChange = (page) => {
    if (page !== currentPage) { appendRef.current = false; setCurrentPage(page); }
  };

  const handleRowsPerPageChange = (newLimit) => {
    appendRef.current = false;
    setRecordsPerPage(newLimit);
    setCurrentPage(1);
  };

  /* Typing does not fire a request per keystroke any more -- the box
     and the field beside it still move at once, and the fetch
     underneath waits for the typing to stop. */
  const runSearch = useDebouncedCallback((value) => {
    appendRef.current = false;
    setSearchTerm(value);
    setCurrentPage(1);
  });

  const onSearchChange = (value, field) => {
    setUiSearchTerm(value);
    setSearchField(field);
    runSearch(value);
  };

  const headers = [
    {
      title: 'Course',
      prop: 'courseName',
      sortable: true,
      filterable: true,
      cell: (row) => (
        <div className="lp-cell">
          <span className="lp-name">{row.courseName}</span>
          {row.branchOfCourse && (
            <span className="lp-sub">{row.branchOfCourse}</span>
          )}
        </div>
      ),
    },
    {
      title: 'Count',
      prop: 'topicCount',
      sortable: true,
      cell: (row) => <span className="lp-num">{row.topicCount}</span>,
    },
    {
      title: 'Duration',
      prop: 'duration',
      cell: (row) => <span className="lp-plain">{row.duration || '—'}</span>,
    },
    {
      title: 'Added',
      prop: 'createdAt',
      sortable: true,
      cell: (row) => (
        <span className="lp-when">
          {row.createdAt ? new Date(row.createdAt).toLocaleDateString() : '—'}
        </span>
      ),
    },
  ];

  const indexedData = data.map((item, idx) => ({
    ...item,
    index: (currentPage - 1) * recordsPerPage + idx + 1,
  }));

  /* A trainer reads the syllabus; they do not change it. The check sits on
     the two flags rather than on the footer buttons alone, so it takes the
     per-topic Edit / Delete and the Actions column with it -- otherwise the
     popup would refuse the syllabus and still hand out its topics. A
     superadmin is never a trainer, whatever the first profile says. */
  const currentUser = user?.user || user;
  const isTrainer =
    currentUser?.isSuperAdmin !== true &&
    currentUser?.profiles?.[0]?.label?.toLowerCase() === 'trainer';

  const canDelete = !isTrainer && hasPermission('list-syllabuses', 'delete', 'lms');
  const canEditTopic = !isTrainer && hasPermission('list-syllabuses', 'update', 'lms');

  return (
    <div className="pg lp">

      <header className="pg-head pg-head--bare">
        <div className="pg-head-actions">
          {hasPermission('list-syllabuses', 'add', 'lms') && (
            <button
              type="button"
              className="pg-btn pg-btn--main"
              onClick={openAddForm}
            >
              Add syllabus
            </button>
          )}
        </div>
      </header>

      <FilterSheet label="Filter syllabuses">
        <div className="pg-filters-search">
          <div className="pg-field pg-field--search">
            <label className="pg-label" htmlFor="lp-syl-search">
              Search
            </label>
            <input
              id="lp-syl-search"
              type="search"
              className="pg-input pg-input--search"
              placeholder="Search by course name..."
              value={uiSearchTerm}
              onChange={(e) => onSearchChange(e.target.value, searchField)}
            />
          </div>

          <div className="pg-field pg-field--tight">
            <label className="pg-label" htmlFor="lp-syl-rows">
              Rows per load
            </label>
            <select
              id="lp-syl-rows"
              className="pg-select"
              value={recordsPerPage}
              onChange={(e) => handleRowsPerPageChange(Number(e.target.value))}
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>
      </FilterSheet>

      <div className="td-list td-list--lms">
        <NewCustomDataTable
          data={indexedData}
          headers={headers}
          totalRecords={totalRecords}
          currentPage={currentPage}
          recordsPerPage={recordsPerPage}
          onPageChange={handlePageChange}
          serverSide={true}
          loading={loading}
          loadingMore={loadingMore}
          showToolbar={false}
          showPagination={false}
          showFooter={false}
          onRowClick={(row) => setDetails(row)}
          validRowClickTagNames="*"
        />
      </div>

      <div className="att-picklist-foot" ref={listEndRef}>
        {loadingMore ? (
          <>
            <span className="pg-spinner att-spinner" aria-hidden="true" />
            <span>Loading more courses...</span>
          </>
        ) : hasMore ? (
          <span>
            {data.length} of {totalRecords} courses
          </span>
        ) : (
          totalRecords > 0 && (
            <span>
              All {totalRecords} course{totalRecords === 1 ? '' : 's'} loaded
            </span>
          )
        )}
      </div>

      {/* Adding topics and renaming one are the same dialog: the course,
          where the topics hang, and the names. Editing is handed the topic
          id and drops the first two to a stated fact. */}
      {formModal && (
        <div
          className="pg-modal pg-modal--over"
          role="dialog"
          aria-modal="true"
          aria-labelledby="lp-syl-form-title"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeForm();
          }}
        >
          <div className="pg-modal-panel pg-modal-panel--wide pg-modal-panel--form">
            <header className="pg-modal-head">
              <div className="pg-modal-heading">
                <span className="pg-modal-eyebrow">Syllabus</span>
                <h3 className="pg-modal-title" id="lp-syl-form-title">
                  {formModal.recordId
                    ? 'Edit topic'
                    : formModal.courseId
                      ? 'Edit syllabus'
                      : 'Add to a syllabus'}
                </h3>
                <p className="pg-modal-sub">
                  {formModal.recordId
                    ? 'Rename this topic. Its course and place in the syllabus stay as they are.'
                    : formModal.courseId
                      ? 'The course is already chosen. Say where the topics hang, and name them.'
                      : 'Pick the course, say where the topics hang, and name them.'}
                </p>
              </div>
              <button
                type="button"
                className="pg-modal-close"
                onClick={closeForm}
                aria-label="Close"
              >
                &times;
              </button>
            </header>

            <div className="pg-modal-body">
              {/* The same component the /syllabus-form route renders --
                  embedded, so it drops its own header and reports back
                  here instead of navigating. */}
              {formDone ? (
                <PopupDone message={formDone} onClose={closeForm} />
              ) : (
              <SyllabusForm
                embedded
                recordId={formModal.recordId}
                courseId={formModal.courseId}
                onCancel={closeForm}
                onDone={() => {
                  // Saved: the form's toast becomes this dialog's done panel,
                  // which closes the dialog by itself.
                  // The sheet behind reloads its topics when it is handed
                  // a new object for the same course.
                  setDetails((d) => (d ? { ...d } : d));
                  // Rebuild from page 1 so a new topic is counted and an
                  // edited one shows its new name.
                  appendRef.current = false;
                  if (currentPage === 1) fetchData(1, searchTerm);
                  else setCurrentPage(1);
                }}
              />
              )}
            </div>
          </div>
        </div>
      )}

      {/* One course's syllabus. Its topics open from here rather than from
          a second clickable cell in the row. */}
      {details && (
        <div
          className="pg-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="lp-syl-title"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setDetails(null);
          }}
        >
          <div className="pg-modal-panel pg-modal-panel--wide">
            <header className="pg-modal-head">
              <div className="pg-modal-heading">
                <span className="pg-modal-eyebrow">Syllabus</span>
                <h3 className="pg-modal-title" id="lp-syl-title">
                  {details.courseName}
                </h3>
                <p className="pg-modal-sub">
                  {details.topicCount} topic{details.topicCount === 1 ? '' : 's'}
                </p>
              </div>
              <button
                type="button"
                className="pg-modal-close"
                onClick={() => setDetails(null)}
                aria-label="Close"
              >
                &times;
              </button>
            </header>

            <div className="pg-modal-body">
              {detailsDone ? (
                <PopupDone message={detailsDone} onClose={() => setDetails(null)} />
              ) : (
              <>
              <dl className="quiz-facts">
                <div>
                  <dt>Duration</dt>
                  <dd>{details.duration || '—'}</dd>
                </div>
                <div>
                  <dt>Branch</dt>
                  <dd>{details.branchOfCourse || '—'}</dd>
                </div>
                <div>
                  <dt>Department</dt>
                  <dd>{details.departmentOfCourse || '—'}</dd>
                </div>
                <div>
                  <dt>Added</dt>
                  <dd>
                    {details.createdAt
                      ? new Date(details.createdAt).toLocaleString()
                      : '—'}
                  </dd>
                </div>
              </dl>

              {details.description && (
                <>
                  <h5 className="pgm-section-title">Description</h5>
                  <p className="lp-prose">{details.description}</p>
                </>
              )}

              <h5 className="pgm-section-title">
                Topics
                <span className="pgm-section-count">{topicRows.length}</span>
              </h5>

              {topicsLoading ? (
                <div className="att-picklist-foot">
                  <span className="pg-spinner att-spinner" aria-hidden="true" />
                  <span>Loading topics...</span>
                </div>
              ) : topicRows.length === 0 ? (
                <p className="lp-muted">No topics on this course yet.</p>
              ) : (
                <div className="lp-dialog-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Sr. No</th>
                        <th>Topic</th>
                        <th>Added</th>
                        {(canEditTopic || canDelete) && <th>Actions</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {topicRows.map((topic, i) => (
                        <tr key={topic.id}>
                          <td>{i + 1}</td>
                          <td>
                            <span
                              className={
                                topic.isChild ? 'lp-topic lp-topic--sub' : 'lp-topic'
                              }
                            >
                              {topic.topic || topic.name || 'Untitled'}
                            </span>
                          </td>
                          <td>
                            {topic.createdAt
                              ? new Date(topic.createdAt).toLocaleDateString()
                              : '—'}
                          </td>
                          {(canEditTopic || canDelete) && (
                            <td>
                              <span className="lp-rowacts">
                                {canEditTopic && (
                                  <button
                                    type="button"
                                    className="pg-link"
                                    onClick={() =>
                                      handleEditTopic(topic.id, details)
                                    }
                                  >
                                    Edit
                                  </button>
                                )}
                                {canDelete && (
                                  <button
                                    type="button"
                                    className="pg-link pg-link--danger"
                                    onClick={() =>
                                      handleDeleteTopic(
                                        topic.id,
                                        topic.topic || topic.name || '',
                                      )
                                    }
                                  >
                                    Delete
                                  </button>
                                )}
                              </span>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              </>
              )}
            </div>

            {/* What you can do with the syllabus sits with Close, at the
                end -- you read it, then act on it. */}
            {!detailsDone && (
            <footer className="pg-modal-foot">
              <div className="pgm-actions">
                {canEditTopic && (
                  <button
                    type="button"
                    className="pgm-btn"
                    onClick={() => handleEditSyllabus(details)}
                  >
                    Edit syllabus
                  </button>
                )}
                {canDelete && (
                  <button
                    type="button"
                    className="pgm-btn pgm-btn--danger"
                    onClick={() => {
                      const row = details;
                      handleDeleteCourse(row);
                    }}
                  >
                    Delete syllabus
                  </button>
                )}
              </div>
              <button
                type="button"
                className="pg-btn pg-btn--main"
                onClick={() => setDetails(null)}
              >
                Close
              </button>
            </footer>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ListSyllabus;
